import {
  Stack,
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  Group,
  SelectProps,
  Text,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { AutoCompleteWithQuery, CountryLabel, LabelWithTooltip } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { SupplierResponses } from "@tfg/types/endpoints/supplier"
import { PaginationType } from "@tfg/types/schemas/utils"

import { IconManufacturing } from "~/components/icons/activities/IconManufacturing"
import { useListSupplierProductionSitesQuery } from "~/pages/SupplierPortal/ProductionSites/hooks/useListProductionSiteQuery.ts"
import { ProductionSiteDrawer } from "~/pages/SupplierPortal/TraceabilityRequests/components/ProductionSiteDrawer.tsx"
import { useAuth } from "~/store/auth-context"
import { COUNTRIES } from "~/utils/countries"

interface Props extends Partial<SelectProps> {
  productionSiteId: string | null
  onProductionSiteSelected: (productionSiteId: string | null) => void
  disabled?: boolean
  isRequired?: boolean
  tooltip?: string
}

export const ProductionSiteSelect = ({
  productionSiteId,
  onProductionSiteSelected,
  disabled,
  isRequired,
  tooltip,
}: Props) => {
  const { t } = useTranslation(["traceability"])

  const [modalOpen, modalActions] = useDisclosure(false)

  const { isAdmin } = useAuth()

  return (
    <>
      <AutoCompleteWithQuery<
        SupplierResponses.ProductionSite,
        PaginationType & { productionSiteId: string | null }
      >
        query={useListSupplierProductionSitesQuery}
        extraParams={{ productionSiteId }}
        parseOptionFunction={(data) =>
          data.map((item) => ({
            label: item.name,
            value: item.id,
            productionSite: item,
          }))
        }
        onCreate={() => modalActions.open()}
        onChange={(value) => {
          if (value) {
            onProductionSiteSelected(value)
          } else {
            onProductionSiteSelected(null)
          }
        }}
        value={productionSiteId}
        error={!productionSiteId && !disabled}
        placeholder={t("productionSiteSelect.placeholders.productionSite")}
        disabled={disabled}
        copyableValue={isAdmin ? "productionSiteId" : undefined}
        isAdmin={isAdmin}
        label={
          <LabelWithTooltip
            justify="space-between"
            label={t("productionSiteSelect.labels.productionSite")}
            hoverHelperText={
              tooltip && tooltip.length > 0 ? tooltip : undefined
            }
            withAsterisk={isRequired}
          />
        }
        createLabel={t("productionSiteSelect.labels.addNewProductionSite")}
        leftSection={<IconManufacturing size={16} />}
        comboboxProps={{ width: 400 }}
        customRender={({
          option,
          checked,
        }: ComboboxLikeRenderOptionInput<
          ComboboxItem & { productionSite?: SupplierResponses.ProductionSite }
        >) => {
          const productionSite = option.productionSite
          return (
            <Group
              justify="space-between"
              data-checked={checked ? "true" : undefined}
              p="sm"
              w="100%"
            >
              <Stack gap={0}>
                <Text fw={700}>{productionSite?.name}</Text>
                {productionSite?.country && (
                  <Group gap="xs">
                    <CountryLabel
                      countryIso3166={productionSite.country}
                      width={12}
                      countriesMapper={COUNTRIES}
                    />
                    <Text size="xs" style={{ width: "100%" }}>
                      {productionSite.address}
                    </Text>
                  </Group>
                )}
              </Stack>
            </Group>
          )
        }}
      />
      {modalOpen && (
        <ProductionSiteDrawer
          defaultName=""
          opened={modalOpen}
          onClose={modalActions.close}
          onProductionSiteCreated={(productionSiteIdCreated) => {
            onProductionSiteSelected(productionSiteIdCreated)
          }}
        />
      )}
    </>
  )
}
